
import React, { useState, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { motion, AnimatePresence } from 'motion/react';

interface HelpArticle {
    id: string;
    title: string;
    description: string;
    category: string;
    steps: string[];
    tips?: string[];
    relatedFeatures?: string[];
}

const HELP_ARTICLES: HelpArticle[] = [
    // Getting Started
    {
        id: 'gs-1',
        category: 'Getting Started',
        title: 'Welcome to Scheduler Pro',
        description: 'Learn the basics of navigating and using the scheduling system.',
        steps: [
            'Log in using your provided email and password. Note: The navigation header is hidden until you log in.',
            'Use the top navigation bar to switch between the Scheduler, Staff List, and Settings.',
            'The Scheduler view shows the current week\'s assignments.',
            'Click on any cell in the scheduler to view or edit shift details.'
        ],
        tips: ['You can use the date picker at the top to jump to any specific week.'],
        relatedFeatures: ['Schedule Management', 'Admin Settings']
    },
    {
        id: 'gs-2',
        category: 'Getting Started',
        title: 'Switching Between Dark and Light Mode',
        description: 'Customize your viewing experience based on your preference.',
        steps: [
            'Go to the Settings page from the top navigation.',
            'Find the "Appearance" or "Theme" section.',
            'Select "Light", "Dark", or "System" (which follows your device settings).',
            'The changes are applied instantly.'
        ],
        relatedFeatures: ['Admin Settings']
    },
    {
        id: 'gs-3',
        category: 'Getting Started',
        title: 'Staff Portal Overview',
        description: 'A dedicated interface for employees to manage their schedules and requests.',
        steps: [
            'Log in with your staff credentials.',
            'View your personal schedule for the current and upcoming weeks.',
            'Manage your availability and set date-specific overrides.',
            'Submit and track time off or cover requests.',
            'Book shuttle reservations for guests.',
            'Note: Parking management is handled exclusively by administrators.'
        ],
        relatedFeatures: ['Time Off Requests', 'Shuttle Reservation']
    },
    // Staff Management
    {
        id: 'sm-1',
        category: 'Staff Management',
        title: 'Adding a New Staff Member',
        description: 'How to register new employees in the system.',
        steps: [
            'Navigate to the "Staff Management" page.',
            'Click the "Add Staff" button at the top right.',
            'Fill in the name, email, role, and department.',
            'Set their default availability for each day of the week.',
            'Click "Save" to add them to the directory.'
        ],
        tips: ['Make sure to use a unique email address for each staff member.'],
        relatedFeatures: ['Schedule Management']
    },
    {
        id: 'sm-2',
        category: 'Staff Management',
        title: 'Editing Staff Details',
        description: 'Update existing employee information or availability.',
        steps: [
            'Go to the "Staff Management" page.',
            'Find the staff member in the list and click the "Edit" (pencil) icon.',
            'Update the necessary fields in the modal that appears.',
            'Click "Save Changes" to apply the updates.'
        ],
        relatedFeatures: ['Time Off Requests']
    },
    // Schedule Management
    {
        id: 'sch-1',
        category: 'Schedule Management',
        title: 'Creating a Weekly Schedule',
        description: 'Assign shifts to staff members for the upcoming week.',
        steps: [
            'Open the "Scheduler" view.',
            'Select the desired week using the date navigation.',
            'Click on a cell corresponding to a staff member and a specific day.',
            'Choose a shift from the dropdown menu (e.g., Morning, Afternoon, Night).',
            'The system saves your changes automatically.'
        ],
        tips: ['Use the "Copy Previous Week" feature to save time if schedules are consistent.'],
        relatedFeatures: ['Staff Management', 'Notifications']
    },
    {
        id: 'sch-2',
        category: 'Schedule Management',
        title: 'Printing the Schedule',
        description: 'Generate a physical copy of the weekly roster.',
        steps: [
            'In the Scheduler view, click the "Print" icon at the top right.',
            'A new window will open with a printer-friendly version of the current view.',
            'Use your browser\'s print dialog (Ctrl+P or Cmd+P) to print or save as PDF.'
        ]
    },
    // Shuttle Reservation
    {
        id: 'shu-1',
        category: 'Shuttle Reservation',
        title: 'Booking a Shuttle for Guests',
        description: 'Manage guest transportation to and from the hotel.',
        steps: [
            'Navigate to the "Shuttle" page.',
            'Click "New Reservation".',
            'Select the direction (To Hotel or From Hotel).',
            'Enter the guest name, room number, number of guests, and the pickup location.',
            'Set the date and time for the shuttle.',
            'Click "Confirm Reservation".'
        ],
        tips: ['Always double-check the guest count to ensure the shuttle has enough capacity.'],
        relatedFeatures: ['Notifications']
    },
    // Parking Management
    {
        id: 'prk-1',
        category: 'Parking Management',
        title: 'Managing Parking Reservations and Statuses',
        description: 'Understand the lifecycle of a parking reservation from booking to checkout.',
        steps: [
            'Navigate to the "Parking" page.',
            'New reservations are created with a default status of "Reservation".',
            'Click the "View" (eye) icon on any record to open the preview modal.',
            'In the preview modal, you can update the status to "Check-in" or "Checkout".',
            'Reservations are automatically marked as "Inactive" if they are checked out or if the end date passed more than 2 days ago.'
        ],
        tips: ['Use the "Show Inactive" toggle in the header to filter the list.'],
        relatedFeatures: ['Admin Settings']
    },
    // Time Off Requests
    {
        id: 'to-1',
        category: 'Time Off Requests',
        title: 'Submitting a Time Off Request',
        description: 'How staff can request leave through the portal.',
        steps: [
            'Log in to the Staff Portal.',
            'Click on "Request Time Off".',
            'Select the start and end dates.',
            'Provide a reason for the request.',
            'Submit the form for manager approval.'
        ],
        tips: ['Submit requests at least two weeks in advance for better planning.'],
        relatedFeatures: ['Schedule Management', 'Notifications']
    },
    {
        id: 'to-2',
        category: 'Time Off Requests',
        title: 'Approving or Rejecting Requests',
        description: 'Manager instructions for handling leave applications.',
        steps: [
            'Go to the "Time Off" management page.',
            'Review the list of "Pending" requests.',
            'Click "Approve" or "Reject" on the specific request.',
            'The staff member will receive a notification of the decision.'
        ]
    },
    // Notifications
    {
        id: 'not-1',
        category: 'Notifications',
        title: 'Managing Your Notifications',
        description: 'Stay updated on schedule changes and requests.',
        steps: [
            'Click the "Bell" icon in the top header to open the notification panel.',
            'Unread notifications are highlighted.',
            'Click on a notification to mark it as read or view details.',
            'Use "Mark all as read" to clear the list quickly.'
        ]
    },
    // Admin Settings
    {
        id: 'set-1',
        category: 'Admin Settings',
        title: 'Managing Departments and Shifts',
        description: 'Configure the core structure of your hotel operations.',
        steps: [
            'Navigate to the "Settings" page.',
            'Under "Departments", you can add, edit, or delete hotel areas (e.g., Front Desk, Housekeeping).',
            'Under "Shifts", define the working hours for different time slots.',
            'Changes here will reflect in the Scheduler and Staff Management views.'
        ],
        tips: ['Assigning unique colors to departments helps in visual scheduling.'],
        relatedFeatures: ['Staff Management', 'Schedule Management']
    },
    // Troubleshooting
    {
        id: 'tr-1',
        category: 'Troubleshooting',
        title: 'Common Login Issues',
        description: 'What to do if you cannot access your account.',
        steps: [
            'Ensure your email address is typed correctly without extra spaces.',
            'Check if "Caps Lock" is on while typing your password.',
            'If you forgot your password, click "Forgot Password" on the login screen.',
            'Contact your system administrator if your account is suspended.'
        ]
    },
    {
        id: 'tr-2',
        category: 'Troubleshooting',
        title: 'Data Not Saving',
        description: 'Steps to take if changes are not persisting.',
        steps: [
            'Check your internet connection.',
            'Refresh the page to ensure you have the latest data.',
            'If you see a "Permission Denied" error, your session may have expired. Try logging out and back in.'
        ]
    }
];

const CATEGORIES = [
    'All',
    'Getting Started',
    'Staff Management',
    'Schedule Management',
    'Shuttle Reservation',
    'Parking Management',
    'Time Off Requests',
    'Notifications',
    'Admin Settings',
    'Troubleshooting'
];

interface HelpCenterViewProps {
    isMobile?: boolean;
}

const HelpCenterView: React.FC<HelpCenterViewProps> = ({ isMobile = false }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

    const filteredArticles = useMemo(() => {
        return HELP_ARTICLES.filter(article => {
            const matchesSearch = 
                article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.category.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesCategory = !selectedCategory || article.category === selectedCategory;
            
            return matchesSearch && matchesCategory;
        });
    }, [searchQuery, selectedCategory]);

    const selectedArticle = useMemo(() => {
        return HELP_ARTICLES.find(a => a.id === selectedArticleId);
    }, [selectedArticleId]);

    const handleBack = () => {
        setSelectedArticleId(null);
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-12 text-center">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">How can we help you?</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                    Search our help center or browse categories below to find answers to your questions.
                </p>
                
                {/* SearchBar */}
                <div className="mt-8 max-w-xl mx-auto relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Icons.Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search for articles, features, or keywords..."
                        className="block w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all dark:text-white"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className={`flex flex-col ${isMobile ? '' : 'lg:flex-row'} gap-8`}>
                {/* Sidebar: CategoryList */}
                <div className={`w-full ${isMobile ? '' : 'lg:w-1/4'} space-y-6`}>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-700">
                        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Categories</h2>
                        <nav className="space-y-1">
                            <button
                                onClick={() => { setSelectedCategory(null); setSelectedArticleId(null); }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!selectedCategory ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                            >
                                All Articles
                            </button>
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => { setSelectedCategory(cat); setSelectedArticleId(null); }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* QuickTipsPanel */}
                    <div className="bg-indigo-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center gap-2 mb-4">
                            <Icons.Info className="h-5 w-5" />
                            <h3 className="font-bold">Quick Tips</h3>
                        </div>
                        <ul className="space-y-3 text-sm text-indigo-100">
                            <li className="flex gap-2">
                                <span className="font-bold">•</span>
                                <span>Use the "Search" bar to find specific topics quickly.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold">•</span>
                                <span>Dark mode can be toggled in the Settings menu.</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold">•</span>
                                <span>Check the "Troubleshooting" section for common fixes.</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Main Content: ArticleViewer or ArticleList */}
                <div className={`w-full ${isMobile ? '' : 'lg:w-3/4'}`}>
                    <AnimatePresence mode="wait">
                        {selectedArticleId && selectedArticle ? (
                            <motion.div
                                key="article"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-slate-700"
                            >
                                <button
                                    onClick={handleBack}
                                    className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium mb-6 hover:underline"
                                >
                                    <Icons.ArrowLeft className="h-4 w-4" />
                                    Back to articles
                                </button>

                                <div className="mb-8">
                                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 mb-4">
                                        {selectedArticle.category}
                                    </span>
                                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{selectedArticle.title}</h2>
                                    <p className="text-lg text-gray-600 dark:text-gray-400">{selectedArticle.description}</p>
                                </div>

                                <div className="space-y-8">
                                    <section>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-xs">1</span>
                                            Step-by-Step Instructions
                                        </h3>
                                        <div className="space-y-4 ml-8">
                                            {selectedArticle.steps.map((step, idx) => (
                                                <div key={idx} className="flex gap-4">
                                                    <span className="text-indigo-600 dark:text-indigo-400 font-bold">{idx + 1}.</span>
                                                    <p className="text-gray-700 dark:text-gray-300">{step}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    {selectedArticle.tips && selectedArticle.tips.length > 0 && (
                                        <section className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-6 rounded-r-xl">
                                            <h4 className="text-amber-800 dark:text-amber-400 font-bold mb-2 flex items-center gap-2">
                                                <Icons.Info className="h-5 w-5" />
                                                Pro Tips
                                            </h4>
                                            <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-1">
                                                {selectedArticle.tips.map((tip, idx) => (
                                                    <li key={idx}>{tip}</li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}

                                    {selectedArticle.relatedFeatures && (
                                        <section className="pt-8 border-t border-gray-100 dark:border-slate-700">
                                            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Related Features</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedArticle.relatedFeatures.map(feat => (
                                                    <span key={feat} className="px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm">
                                                        {feat}
                                                    </span>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="list"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="space-y-6"
                            >
                                {filteredArticles.length > 0 ? (
                                    <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2'} gap-4`}>
                                        {filteredArticles.map(article => (
                                            <button
                                                key={article.id}
                                                onClick={() => setSelectedArticleId(article.id)}
                                                className="text-left bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 transition-all group"
                                            >
                                                <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-2 block">
                                                    {article.category}
                                                </span>
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                                    {article.title}
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                                                    {article.description}
                                                </p>
                                                <div className="mt-4 flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                                    Read article
                                                    <Icons.ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-700">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
                                            <Icons.Search className="h-8 w-8 text-gray-400" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No articles found</h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            We couldn't find any help articles matching "{searchQuery}".
                                            <br />Try using different keywords or browse by category.
                                        </p>
                                        <button
                                            onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                                            className="mt-6 text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
                                        >
                                            Clear all filters
                                        </button>
                                    </div>
                                )}

                                {/* ContactSupportCard */}
                                <div className="mt-12 bg-gray-900 dark:bg-slate-950 rounded-3xl p-8 text-white relative overflow-hidden">
                                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                                        <div className="text-center md:text-left">
                                            <h3 className="text-2xl font-bold mb-2">Still need help?</h3>
                                            <p className="text-gray-400">Our support team is always here to assist you with any technical issues.</p>
                                        </div>
                                        <button className="px-8 py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap">
                                            Contact Support
                                        </button>
                                    </div>
                                    {/* Decorative background element */}
                                    <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl"></div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default HelpCenterView;
